import { authFetch } from '../auth/authFetch';
import { getAccessToken } from '../auth/authSession';
import { isJwtAuth } from '../auth/authMode';
import { UnauthorizedError, redirectToSignIn } from '../auth/identity';

const API_BASE_URL = (
  process.env.REACT_APP_API_BASE_URL || 'http://localhost:8000'
).replace(/\/$/, '');

function buildQueryString(query) {
  const params = new URLSearchParams();
  Object.entries(query || {}).forEach(([key, value]) => {
    if (value !== undefined && value !== null && value !== '') {
      params.set(key, String(value));
    }
  });
  const queryString = params.toString();
  return queryString ? `?${queryString}` : '';
}

function extractErrorMessage(errorPayload, status) {
  const fallback = `Request failed (${status})`;
  if (!errorPayload || typeof errorPayload !== 'object') {
    return fallback;
  }
  if (typeof errorPayload.message === 'string' && errorPayload.message.trim()) {
    return errorPayload.message;
  }
  if (typeof errorPayload.detail === 'string' && errorPayload.detail.trim()) {
    return errorPayload.detail;
  }
  return fallback;
}

function authHeaders() {
  if (!isJwtAuth()) {
    throw new UnauthorizedError('Admin APIs require JWT auth mode');
  }
  const token = getAccessToken();
  if (!token) {
    throw new UnauthorizedError('Sign in required');
  }
  return { Authorization: `Bearer ${token}` };
}

async function parseResponse(response) {
  if (!response.ok) {
    if (response.status === 401) redirectToSignIn();
    let message = `Request failed (${response.status})`;
    try {
      const errorPayload = await response.json();
      message = extractErrorMessage(errorPayload, response.status);
    } catch {
      // keep default
    }
    const error = new Error(message);
    error.status = response.status;
    throw error;
  }
  if (response.status === 204) {
    return null;
  }
  return response.json();
}

async function request(path, { method = 'GET', body, query } = {}) {
  const response = await authFetch(`${API_BASE_URL}${path}${buildQueryString(query)}`, {
    method,
    headers: {
      'Content-Type': 'application/json',
      ...authHeaders(),
    },
    body: body ? JSON.stringify(body) : undefined,
  });
  return parseResponse(response);
}

export function listOrganizations(query) {
  return request('/v1/admin/organizations', { query });
}

export function createOrganization(body) {
  return request('/v1/admin/organizations', { method: 'POST', body });
}

export function updateOrganization(orgId, body) {
  return request(`/v1/admin/organizations/${orgId}`, { method: 'PATCH', body });
}

export function inviteOrgAdmin(orgId, body) {
  return request(`/v1/admin/organizations/${orgId}/invitations`, {
    method: 'POST',
    body,
  });
}

export function listConnections(query) {
  return request('/v1/registry/connections', { query });
}

export function createConnection(body) {
  return request('/v1/admin/connections', { method: 'POST', body });
}

export function deleteConnection(connectionId) {
  return request(`/v1/admin/connections/${connectionId}`, { method: 'DELETE' });
}

export function listRegistryOrganizations(query) {
  return request('/v1/registry/organizations', { query });
}
